import React from 'react';
import { useObjectRef } from 'react-aria';
import { Group, TextArea as AriaTextArea, TextField as AriaTextField } from 'react-aria-components';

import { tv } from '../../tv';
import { Size } from '../../types';
import {
  entryStyles,
  FieldLabel,
  FieldMessage,
  focusEntryOnPress,
  frameState,
  frameStyles,
  resolveFieldState,
  useFieldLayout,
  useFieldMessageIds,
} from '../field/field-shell';

interface TextAreaCommonProps {
  /**
   * Help under the field: the rule the value must meet, or why the field is
   * read-only. Inline content only (Text, Link, Code runs), never blocks.
   * Hidden while `error` shows, since the error restates the rule.
   */
  description?: React.ReactNode;
  /**
   * The error message. The field is invalid exactly when this is non-empty.
   * Say what to do, restating the rule: "Keep the summary under 500
   * characters", never "Too long".
   */
  error?: string;
  /** Marks the label with an asterisk and exposes `aria-required`. */
  isRequired?: boolean;
  /**
   * Washed out and skipped by Tab. Prefer `isReadOnly` whenever the value
   * still matters to the reader.
   */
  isDisabled?: boolean;
  /** Full contrast and focusable, but not editable. Say why in `description`. */
  isReadOnly?: boolean;
  /** The value (controlled). */
  value?: string;
  /** The initial value (uncontrolled). */
  defaultValue?: string;
  /** Called with the new value on every change. */
  onChange?: (value: string) => void;
  /** Called when the textarea loses focus. */
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** Called when the textarea receives focus. */
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** Keys pressed in the textarea. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /** Example input, never a substitute for the label. */
  placeholder?: string;
  /** The form field name. */
  name?: string;
  /** The id of the textarea element. */
  id?: string;
  /** Whether the textarea takes focus on mount. */
  autoFocus?: boolean;
  /** Text size and inset: sm 12px, md 14px, lg 16px text. */
  size?: Size;
  /** The height before any typing, in lines. The field grows from here. */
  minRows?: number;
  /** The height the field stops growing at, in lines; then it scrolls. */
  maxRows?: number;
  /**
   * A soft limit: a count shows at the end of the message line and turns to
   * the error colour past the limit, but typing and pasting are never cut
   * off. Enforce the limit in the form's validation, whose message then
   * shows as `error`.
   */
  characterLimit?: number;
}

interface TextAreaLabelledProps {
  /** The visible name, above the field or beside it (Form decides). */
  label: string;
  'aria-label'?: never;
  'aria-labelledby'?: never;
}

interface TextAreaUnlabelledProps {
  label?: never;
  /** The accessible name of a field with no visible label. */
  'aria-label': string;
  'aria-labelledby'?: never;
}

interface TextAreaLabelledByProps {
  label?: never;
  'aria-label'?: never;
  /** The id of the visible element that names the field (a question's text). */
  'aria-labelledby': string;
}

export type TextAreaProps = TextAreaCommonProps &
  (TextAreaLabelledProps | TextAreaUnlabelledProps | TextAreaLabelledByProps);

const countStyles = tv({
  base: 'ck:shrink-0 ck:text-xs ck:tabular-nums',
  variants: {
    isOver: {
      true: 'ck:text-error-text',
      false: 'ck:text-text-muted',
    },
  },
});

/* Announced once typing pauses, so a screen reader hears the count without
 * an announcement per keystroke. */
const ANNOUNCE_DELAY_MS = 800;

/**
 * Multi-line text with its label, help and error. It grows with its content
 * from `minRows` to `maxRows`, then scrolls; there is no resize handle, which
 * would fight the growth.
 *
 * Same contract as TextField: `aria` validation owned by the form, and the
 * forwarded ref is the `<textarea>`.
 */
export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    label,
    description,
    error: errorProp,
    isRequired: requiredProp,
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
    size = 'md',
    minRows = 3,
    maxRows = 12,
    characterLimit,
    placeholder,
    value,
    defaultValue,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    ...props
  },
  ref,
) {
  const { error, isRequired, isDisabled, isReadOnly, isInvalid } = resolveFieldState({
    error: errorProp,
    isRequired: requiredProp,
    isDisabled: disabledProp,
    isReadOnly: readOnlyProp,
  });
  const textAreaRef = useObjectRef(ref);
  const { labelPosition, styles } = useFieldLayout(size);
  const { root, label: labelClass, column } = styles;
  const { descriptionId, errorId, describedBy } = useFieldMessageIds(description, error);

  const [uncontrolledLength, setUncontrolledLength] = React.useState(defaultValue?.length ?? 0);
  const length = value !== undefined ? value.length : uncontrolledLength;
  const count = characterLimit === undefined ? '' : `${length}/${characterLimit}`;
  /* Seeded with the first count, so the live region changes only once
   * someone has typed: a region that fills after mount is an announcement. */
  const [announced, setAnnounced] = React.useState(count);
  React.useEffect(() => {
    const timer = setTimeout(() => setAnnounced(count), ANNOUNCE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [count]);

  return (
    <AriaTextField
      {...props}
      value={value}
      defaultValue={defaultValue}
      onChange={(next) => {
        if (value === undefined) setUncontrolledLength(next.length);
        onChange?.(next);
      }}
      isRequired={isRequired}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      isInvalid={isInvalid}
      validationBehavior="aria"
      aria-describedby={describedBy}
      className={root()}
      data-component="text-area"
      data-label-position={labelPosition}
    >
      {label ? (
        <FieldLabel isRequired={isRequired} className={labelClass()}>
          {label}
        </FieldLabel>
      ) : null}
      <div className={column()}>
        <Group
          className={frameStyles({
            size,
            multiline: true,
            hasAction: false,
            state: frameState({ isDisabled, isReadOnly, isInvalid }),
          })}
          onMouseDown={focusEntryOnPress(textAreaRef)}
        >
          {/* On the TextArea, not the field, for the reason TextField gives. */}
          <AriaTextArea
            ref={textAreaRef}
            /* The fallback where field-sizing is unsupported: a fixed
             * minRows box that scrolls. */
            rows={minRows}
            className={entryStyles({ size, multiline: true, readOnly: isReadOnly })}
            /* Computed from props, so inline (escape-hatches.md). lh is the
             * textarea's own line height: exact rows at every size. */
            style={{ minHeight: `${minRows}lh`, maxHeight: `${maxRows}lh` }}
            placeholder={placeholder}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
          />
        </Group>
        <FieldMessage
          description={description}
          error={error}
          descriptionId={descriptionId}
          errorId={errorId}
          trailing={
            characterLimit === undefined ? undefined : (
              <span className={countStyles({ isOver: length > characterLimit })}>
                {/* GOV.UK's pattern: the visible count changes per keystroke
                 * and is hidden, the polite copy follows once typing pauses. */}
                <span aria-hidden="true">{count}</span>
                <span className="ck:sr-only" aria-live="polite">
                  {announced}
                </span>
              </span>
            )
          }
        />
      </div>
    </AriaTextField>
  );
});
